import Cookies from 'universal-cookie';
import { CONFIG } from '@frameio/config';
import { ReactiveMap } from '@frameio/graphql-client/utils/ReactiveMap';
import { isServer } from '@frameio/utils/config/helpers';

export const DEV_FLAG_COOKIE = 'devFlags' as const;

// WARNING: WHEN DEV FLAGS DISABLED (PROD/STAGING) ALL FLAGS RETURN FALSE
const DevFlagsDefaults = {
  BE_PREVIEW_ENVIRONMENT: true,
  ENABLE_WMID_DEBUG_MODE: false,
  ENABLE_VERCEL_TOOLBAR: false,
  PREFETCH_DECREASE: false,
  PREFETCH_INCREASE: false,
  SEGMENT_DEBUGGER: false,
  UPLOADER_CONFIG_OVERRIDES: false,
  UPLOADER_DEBUG_MODE: false,
  ADAPTIVE_THEMING_DENSITY_SIZING: false,
  ARCHON_DEV_TOOLS: false,
  ENABLE_IFRAME_MESSAGES_LOGGING: false,
} as const;

export type DevFlag = keyof typeof DevFlagsDefaults;

export const AvailableDevFlags: Record<
  DevFlag,
  { title: string; description: string; exposeServerSide?: boolean }
> = {
  BE_PREVIEW_ENVIRONMENT: {
    description: 'Display a banner when backend preview environment is in use.',
    title: 'BE Preview Environment Banner',
  },
  ENABLE_VERCEL_TOOLBAR: {
    description:
      'Enables the Vercel Toolbar. NOTE: Run [vercel link] to see in local dev.',
    title: 'Vercel Toolbar',
  },
  PREFETCH_DECREASE: {
    description: 'Prefetch less aggressively in usePrefetch',
    title: 'Prefetch: Decrease',
  },
  PREFETCH_INCREASE: {
    description:
      'Prefetch more aggressively in usePrefetch (note: decrease flag takes precedence)',
    title: 'Prefetch: Increase',
  },
  SEGMENT_DEBUGGER: {
    description:
      'Logs any Segment identify, group or track calls in the browser console.',
    title: 'Segment Debugger',
  },
  UPLOADER_CONFIG_OVERRIDES: {
    description:
      'Enable uploader configuration overrides used for testing purposes',
    title: 'Customize uploader settings',
  },
  ENABLE_WMID_DEBUG_MODE: {
    description: 'Enable WMID debug mode',
    title: 'WMID debug mode',
  },
  UPLOADER_DEBUG_MODE: {
    description: 'Enable uploader debug mode to show state machine logs',
    title: 'Uploader Debug Mode',
  },
  ADAPTIVE_THEMING_DENSITY_SIZING: {
    description:
      'Enable adaptive theming for density and sizing (accessed via the theme selection menu)',
    title: 'Adaptive Theming for Density and Sizing',
  },
  ENABLE_IFRAME_MESSAGES_LOGGING: {
    description: 'Enable console logging of iframe messages',
    title: 'Iframe Message Logging',
  },
  ARCHON_DEV_TOOLS: {
    description: 'Enable developer tools for Archon agent interactions',
    title: 'Archon Agent Dev Tools',
    exposeServerSide: true,
  },
};

const devFlagsMap = new ReactiveMap<DevFlag, boolean, 'devFlagsMap'>(
  'devFlagsMap',
  // enables flags which are true by default in non-production environments
  Object.entries<boolean>(DevFlagsDefaults).filter(
    (arg): arg is [DevFlag, boolean] => CONFIG.allowClientDevFlags && arg[1]
  )
);
syncCookies();

function syncCookies() {
  if (!CONFIG.enableXSideDevFlags || isServer) return;

  const cookieFlags = devFlagsMap
    .filter(([flag, on]) =>
      Boolean(on && AvailableDevFlags[flag]?.exposeServerSide)
    )
    .map(([flag]) => flag);

  const cookieStore = new Cookies();
  const cookieOptions: {
    domain: string;
    sameSite: 'lax';
    expires: Date;
    secure: boolean;
    path: string;
  } = {
    domain: window.location.hostname, // Dev Flags store to host domain
    sameSite: 'lax',
    expires: new Date(2147483647 * 1000), // max date, 2038
    secure: true, // this only allows the cookie to be set over HTTPS
    path: '/',
  };
  if (cookieFlags.length > 0) {
    cookieStore.set(DEV_FLAG_COOKIE, cookieFlags.join(';'), cookieOptions);
  } else {
    cookieStore.remove(DEV_FLAG_COOKIE, cookieOptions);
  }
}

export const setDevFlag = (flag: DevFlag, value: boolean) => {
  devFlagsMap.set(flag, value);
  syncCookies();
};

export const removeDevFlag = (flag: DevFlag) => {
  devFlagsMap.delete(flag);
};

export const getDevFlag = (flag: DevFlag): boolean => {
  if (!CONFIG.allowClientDevFlags) return false;
  return devFlagsMap.get(flag);
};

export const getDevFlags = (): DevFlag[] => {
  if (isServer) {
    return [];
  }
  if (!CONFIG.allowClientDevFlags) {
    return [];
  }
  const flags: DevFlag[] = [];
  devFlagsMap.forEach((on, flag) => {
    if (on) {
      flags.push(flag);
    }
  });
  return flags;
};
